// src/features/document/DocumentComplianceRail.tsx
// Right-rail surface for everything ABOUT a document: lifecycle, versions,
// acknowledgments, approval pipeline, register, audit. Opening it must not lose
// the editor. All lifecycle/version/ack/pipeline RPCs are target-addressed
// (target_type = "document", target_id = the document id).

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useAuth, useDocument, useI18n } from '@react/hooks';
import { Badge, Button } from '@react/components';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { AcknowledgmentRecipients, type AckRecipient } from './AcknowledgmentRecipients';
import {
  ackActorId,
  findPendingForUser,
  groupAcknowledgmentsByVersion,
  parseDocumentVersion,
} from './acknowledgment';
import { AcknowledgmentCeremony } from './AcknowledgmentCeremony';
import { DocumentVersionsPanel } from './DocumentVersionsPanel';
import { DocumentSigningPanel } from './DocumentSigningPanel';
import { UserName } from '@/features/userprofile/UserName';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  formatDate,
  formatRelativeTime,
  getDocumentStatus,
  getDocumentVersion,
  documentStatusMap,
  getStatusMapping,
} from './logic';
import { EntityAvatar } from '@/components/EntityAvatar';
import {
  documentTarget,
  lifecycleAbilities,
  acknowledgmentStatusMap,
  pipelineExecutionStatusMap,
  stageExecutionStatusMap,
  pipelineActionMap,
  registrationDirectionLabelKey,
  mapping,
  ackRollup,
} from './complianceLogic';

import { DocumentReviewPanel, type DocumentReviewAnchor } from './DocumentReviewPanel';
import { canEditDocument } from './canEdit';

type RailTab = 'lifecycle' | 'versions' | 'review' | 'acks' | 'approval' | 'signing' | 'register' | 'audit';

interface Props {
  documentId: string;
  productId: string;
  item: any;
  reviewAnchor?: DocumentReviewAnchor | null;
  onClose: () => void;
  /** Refetch the parent document after a lifecycle change. */
  onChanged: () => void;
  onSuggestionAccepted?: () => void;
}

function actorName(actor: any): string {
  return actor?.display_name || actor?.name || actor?.entity?.id || '';
}

function Avatar({ name, image, profileId }: { name: string; image?: string; profileId?: string }) {
  if (!name) return null;
  return <EntityAvatar name={name} image={image} profileId={profileId} size="xs" className="h-5 w-5 text-[9px]" />;
}

function SectionState({ loading, error, empty, emptyText, children }: {
  loading: boolean;
  error: unknown;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  if (loading) {
    return (
      <div className="space-y-2 animate-pulse py-2" aria-busy="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-9 rounded bg-muted" />
        ))}
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-destructive py-3">{t('common.loadError')}</p>;
  }
  if (empty) {
    return <p className="text-sm text-muted-foreground py-6 text-center">{emptyText}</p>;
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Lifecycle tab
// ---------------------------------------------------------------------------

const LifecycleTab = observer(({ documentId, productId, item, onChanged }: Omit<Props, 'onClose'>) => {
  const { t } = useI18n();
  const store = useDocument();
  const status = getDocumentStatus(item);
  const ab = lifecycleAbilities(status);
  const statusInfo = getStatusMapping(documentStatusMap, status);
  const target = documentTarget(documentId, productId);

  const [busy, setBusy] = useState<string | null>(null);
  const [reviseSummary, setReviseSummary] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [obsoletePrevious, setObsoletePrevious] = useState(true);
  const [confirm, setConfirm] = useState<null | 'archive'>(null);

  const run = useCallback(
    async (name: string, fn: () => Promise<unknown>) => {
      setBusy(name);
      try {
        await fn();
        onChanged();
      } catch {
        /* surfaced by store.error */
      } finally {
        setBusy(null);
      }
    },
    [onChanged],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{t('document.fieldStatus')}</span>
        <Badge variant={statusInfo.variant as any}>{t(statusInfo.labelKey)}</Badge>
      </div>

      <div className="space-y-2">
        <button
          type="button"
          disabled={!ab.canSubmit || busy !== null}
          onClick={() => run('submit', () => store.SubmitForApproval('document', documentId, { target } as any))}
          className="w-full px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
        >
          {busy === 'submit' ? t('common.saving') : t('document.lifecycle.submit')}
        </button>

        <div className="rounded-md border border-border p-2.5 space-y-2">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={obsoletePrevious}
              onChange={(e) => setObsoletePrevious(e.target.checked)}
              className="rounded border-border"
            />
            {t('document.lifecycle.obsoletePrevious')}
          </label>
          <button
            type="button"
            disabled={!ab.canPublish || busy !== null}
            onClick={() =>
              run('publish', () =>
                store.PublishDocument('document', documentId, { target, obsolete_previous: obsoletePrevious } as any),
              )
            }
            className="w-full px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            {busy === 'publish' ? t('common.saving') : t('document.lifecycle.publish')}
          </button>
        </div>

        <div className="rounded-md border border-border p-2.5 space-y-2">
          <input
            type="text"
            value={reviseSummary}
            onChange={(e) => setReviseSummary(e.target.value)}
            placeholder={t('document.lifecycle.changeSummaryPlaceholder')}
            className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="button"
            disabled={!ab.canRevise || busy !== null}
            onClick={() =>
              run('revise', () =>
                store.CreateRevision('document', documentId, { target, change_summary: reviseSummary || undefined } as any),
              )
            }
            className="w-full px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors"
          >
            {busy === 'revise' ? t('common.saving') : t('document.lifecycle.revise')}
          </button>
        </div>

        <button
          type="button"
          disabled={!ab.canArchive || busy !== null}
          onClick={() => setConfirm('archive')}
          className="w-full px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors"
        >
          {t('document.lifecycle.archive')}
        </button>
      </div>

      <div className="rounded-md border border-border p-2.5 space-y-2">
        <div className="text-sm font-medium text-foreground">{t('document.lifecycle.assignNumber')}</div>
        <input
          type="text"
          value={manualNumber}
          onChange={(e) => setManualNumber(e.target.value)}
          placeholder={t('document.lifecycle.manualNumberPlaceholder')}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
        <button
          type="button"
          disabled={busy !== null}
          onClick={() =>
            run('number', () =>
              store.AssignDocumentNumber('document', documentId, {
                target,
                manual_number: manualNumber.trim() || undefined,
              } as any),
            )
          }
          className="w-full px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors"
        >
          {busy === 'number' ? t('common.saving') : t('document.lifecycle.assignNumberAction')}
        </button>
      </div>

      <ConfirmDialog
        open={confirm === 'archive'}
        title={t('document.lifecycle.archiveConfirmTitle')}
        description={t('document.lifecycle.archiveConfirmBody')}
        confirmLabel={t('document.lifecycle.archive')}
        destructive
        isConfirming={busy === 'archive'}
        onConfirm={() => run('archive', () => store.ArchiveDocument('document', documentId, { target } as any)).then(() => setConfirm(null))}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Acknowledgments tab
// ---------------------------------------------------------------------------

const AcksTab = observer(({ documentId, productId, item, onChanged }: Omit<Props, 'onClose'>) => {
  const { t, locale } = useI18n();
  const { activeProfileId } = useAuth();
  const store = useDocument();
  const [acks, setAcks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [recipients, setRecipients] = useState<AckRecipient[]>([]);
  const [message, setMessage] = useState('');
  const [ceremonyOpen, setCeremonyOpen] = useState(false);
  const target = documentTarget(documentId, productId);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    store
      .ListAcknowledgments('document', documentId, { pagination: { limit: 1000, cursor: '', direction: '' } })
      .then((res: any) => setAcks(res?.acknowledgments?.acknowledgments ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [documentId, store]);

  useEffect(() => { load(); }, [load]);

  const groups = groupAcknowledgmentsByVersion(acks);
  const documentVersion = parseDocumentVersion(getDocumentVersion(item)) || groups[0]?.version || 0;
  const pending = findPendingForUser(acks, activeProfileId ?? '', documentVersion);

  const request = () => {
    if (recipients.length === 0 || busy) return;
    setBusy(true);
    store
      .RequestAcknowledgment('document', documentId, {
        target,
        actors: { actors: recipients.map((recipient) => ({ entity: { id: recipient.id, type_name: 'profile' }, display_name: recipient.name })) } as any,
        message: message.trim() || undefined,
        document_version: documentVersion || undefined,
      } as any)
      .then(() => { setRecipients([]); setMessage(''); load(); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border p-2.5 space-y-2">
        <div className="text-sm font-medium text-foreground">{t('document.ack.request')}</div>
        <AcknowledgmentRecipients documentId={documentId} value={recipients} onChange={setRecipients} />
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('document.ack.messagePlaceholder')}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
        <button
          type="button"
          disabled={recipients.length === 0 || busy}
          onClick={request}
          className="w-full px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
        >
          {busy ? t('common.saving') : t('document.ackSendRequest')}
        </button>
      </div>

      {pending && (
        <Button type="button" variant="secondary" className="w-full" onClick={() => setCeremonyOpen(true)}>
          {t('document.ack.openAndConfirm')}
        </Button>
      )}

      {error != null && (
        <Button type="button" variant="secondary" size="sm" onClick={load}>{t('errors.tryAgain')}</Button>
      )}
      <SectionState loading={loading} error={error} empty={groups.length === 0} emptyText={t('document.ack.empty')}>
        <div className="space-y-4">
          {groups.map((group) => {
            const rollup = ackRollup(group.items);
            return (
              <section key={group.version} aria-label={t('document.ack.versionHeading', { version: group.version })}>
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-sm font-medium tabular-nums text-foreground">
                    {group.version > 0
                      ? t('document.ack.versionHeading', { version: group.version })
                      : t('document.ack.legacyVersion')}
                  </h3>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {t('document.ack.progress', { done: rollup.done, total: rollup.total })}
                  </span>
                </div>
                <div className="mb-2 h-1.5 rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={rollup.percent}>
                  <div className={`h-full rounded-full ${rollup.percent === 100 ? 'bg-success' : 'bg-primary'}`} style={{ width: `${rollup.percent}%` }} />
                </div>
                <ul className="divide-y divide-border border-y border-border">
                  {group.items.map((ack, index) => {
                    const info = mapping(acknowledgmentStatusMap, ack.status);
                    const name = ack.actor?.display_name || t('document.ackLegacyUnknown');
                    return (
                      <li key={ack.entity?.id || index} className="flex items-center gap-2 py-2">
                        <Avatar name={name} image={ack.actor?.avatar} profileId={ackActorId(ack)} />
                        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{ackActorId(ack) && !ack.actor?.display_name
                          ? <UserName id={ackActorId(ack)} showIdFallback={false} fallbackLabel={t('document.ackLegacyUnknown')} linkToProfile />
                          : name}</span>
                        <Badge variant={info.variant as any} size="sm">{t(info.labelKey)}</Badge>
                        {ack.acknowledged_at && (
                          <span className="text-[11px] text-muted-foreground">{formatRelativeTime(ack.acknowledged_at, locale)}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </SectionState>

      {ceremonyOpen && pending && (
        <AcknowledgmentCeremony
          documentId={documentId}
          productId={productId}
          documentTitle={item?.title || t('common.untitled')}
          acknowledgment={pending}
          onCancel={() => setCeremonyOpen(false)}
          onCompleted={() => {
            setCeremonyOpen(false);
            load();
            onChanged();
          }}
        />
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Approval (pipeline) tab
// ---------------------------------------------------------------------------

const ApprovalTab = observer(({ documentId, productId, onChanged }: Omit<Props, 'onClose' | 'item'>) => {
  const { t, locale } = useI18n();
  const store = useDocument();
  const [executions, setExecutions] = useState<any[]>([]);
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pipelineId, setPipelineId] = useState('');
  const [comment, setComment] = useState('');
  const [delegateTo, setDelegateTo] = useState('');
  const target = documentTarget(documentId, productId);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      store.ListPipelineExecutions('document', documentId).then((r: any) => r?.executions?.executions ?? []),
      store.ListPipelines().then((r: any) => r?.pipelines?.pipelines ?? []).catch(() => []),
    ])
      .then(([ex, pl]) => { setExecutions(ex); setPipelines(pl); })
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [documentId, productId, store]);

  useEffect(() => { load(); }, [load]);

  const active = executions.find(
    (e) => e.status === 'PIPELINE_EXECUTION_STATUS_IN_PROGRESS' || e.status === 'PIPELINE_EXECUTION_STATUS_PENDING',
  );

  const refreshActive = () => {
    if (!active?.entity?.id) return;
    store
      .GetPipelineExecution('document', documentId, active.entity.id)
      .then((res: any) => {
        const fresh = res?.execution ?? res;
        if (fresh?.entity?.id) setExecutions((prev) => prev.map((e) => (e.entity?.id === fresh.entity.id ? fresh : e)));
      })
      .catch(() => {});
  };

  const start = () => {
    if (!pipelineId) return;
    setBusy('start');
    store
      .StartPipeline('document', documentId, pipelineId, { target, pipeline_id: pipelineId } as any)
      .then(() => { onChanged(); load(); })
      .catch(() => {})
      .finally(() => setBusy(null));
  };

  const act = (result: string) => {
    if (!active) return;
    setBusy(result);
    store
      .ProcessStageAction('document', documentId, active.entity?.id ?? '', {
        target,
        execution_id: active.entity?.id ?? '',
        action_result: result,
        comment: comment.trim() || undefined,
      } as any)
      .then(() => { setComment(''); onChanged(); load(); })
      .catch(() => {})
      .finally(() => setBusy(null));
  };

  const delegate = () => {
    if (!active || !delegateTo) return;
    setBusy('delegate');
    store
      .DelegateStageAction('document', documentId, active.entity?.id ?? '', {
        target,
        execution_id: active.entity?.id ?? '',
        delegate_to: { entity: { id: delegateTo, type_name: 'profile' } } as any,
        comment: comment.trim() || undefined,
      } as any)
      .then(() => { setDelegateTo(''); load(); })
      .catch(() => {})
      .finally(() => setBusy(null));
  };

  const cancel = () => {
    if (!active) return;
    setBusy('cancel');
    store
      .CancelPipelineExecution('document', documentId, active.entity?.id ?? '', {} as any)
      .then(() => { onChanged(); load(); })
      .catch(() => {})
      .finally(() => setBusy(null));
  };

  return (
    <SectionState loading={loading} error={error} empty={false} emptyText="">
      {!active && (
        <div className="rounded-md border border-border p-2.5 space-y-2 mb-3">
          <div className="text-sm font-medium text-foreground">{t('document.pipeline.start')}</div>
          {pipelines.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('document.pipeline.noDefinitions')}</p>
          ) : (
            <>
              <Select
                value={pipelineId}
                onChange={(e: any) => setPipelineId(e.target.value)}
                placeholder={t('document.pipeline.pickDefinition')}
                aria-label={t('document.pipeline.pickDefinition')}
              >
                {pipelines.map((p) => (
                  <option key={p.entity?.id} value={p.entity?.id}>{p.name}</option>
                ))}
              </Select>
              <button
                type="button"
                disabled={!pipelineId || busy !== null}
                onClick={start}
                className="w-full px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
              >
                {busy === 'start' ? t('common.saving') : t('document.pipeline.startAction')}
              </button>
            </>
          )}
        </div>
      )}

      {active && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate flex-1">{active.pipeline_name}</span>
            <Badge variant={mapping(pipelineExecutionStatusMap, active.status).variant as any} size="sm">
              {t(mapping(pipelineExecutionStatusMap, active.status).labelKey)}
            </Badge>
            <button
              type="button"
              onClick={refreshActive}
              aria-label={t('document.pipeline.refresh')}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>

          <ol className="space-y-1.5">
            {(active.stage_executions ?? []).map((s: any, i: number) => {
              const isCurrent = i === active.current_stage_index;
              const sInfo = mapping(stageExecutionStatusMap, s.status);
              const aInfo = s.action ? mapping(pipelineActionMap, s.action) : null;
              return (
                <li
                  key={s.stage_id || i}
                  className={`rounded-md border p-2 ${isCurrent ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-foreground truncate flex-1">{i + 1}. {s.stage_name}</span>
                    {aInfo && <Badge variant={aInfo.variant as any} size="sm">{t(aInfo.labelKey)}</Badge>}
                    <Badge variant={sInfo.variant as any} size="sm">{t(sInfo.labelKey)}</Badge>
                  </div>
                  {s.total_count > 0 && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {t('document.pipeline.stageProgress', { done: s.completed_count, total: s.total_count })}
                      {s.rejected_count > 0 && ` · ${t('document.pipeline.rejectedCount', { n: s.rejected_count })}`}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="rounded-md border border-border p-2.5 space-y-2">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('document.pipeline.commentPlaceholder')}
              rows={2}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring resize-none"
            />
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => act('ACTOR_EXECUTION_STATUS_COMPLETED')}
                className="px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
              >
                {t('document.pipeline.approveAct')}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => act('ACTOR_EXECUTION_STATUS_REJECTED')}
                className="px-3 py-2 text-sm font-medium rounded-md border border-destructive text-destructive hover:bg-destructive/10 disabled:opacity-40 transition-colors"
              >
                {t('document.pipeline.rejectAct')}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <UserPicker value={delegateTo} onChange={(id) => setDelegateTo(id)} scope={{ typeName: 'document', id: documentId }} restrictToScope valueKind="profile" placeholder={t('document.pipeline.delegateTo')} />
              </div>
              <button
                type="button"
                disabled={!delegateTo || busy !== null}
                onClick={delegate}
                className="px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors"
              >
                {t('document.pipeline.delegateAct')}
              </button>
            </div>
            <button
              type="button"
              disabled={busy !== null}
              onClick={cancel}
              className="w-full px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 rounded-md transition-colors"
            >
              {t('document.pipeline.cancel')}
            </button>
          </div>
        </div>
      )}

      {executions.filter((e) => e !== active).length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t('document.pipeline.history')}</div>
          <ul className="space-y-1">
            {executions.filter((e) => e !== active).map((e, i) => (
              <li key={e.entity?.id || i} className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="truncate flex-1">{e.pipeline_name}</span>
                <Badge variant={mapping(pipelineExecutionStatusMap, e.status).variant as any} size="sm">
                  {t(mapping(pipelineExecutionStatusMap, e.status).labelKey)}
                </Badge>
                <span>{formatDate(e.completed_at || e.created_at, locale)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionState>
  );
});

// ---------------------------------------------------------------------------
// Register tab
// ---------------------------------------------------------------------------

const RegisterTab = observer(({ documentId, productId }: Omit<Props, 'onClose' | 'item' | 'onChanged'>) => {
  const { t, locale } = useI18n();
  const store = useDocument();
  const [registers, setRegisters] = useState<any[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [registerId, setRegisterId] = useState('');
  const [summary, setSummary] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    store
      .ListDocumentRegisters()
      .then((res: any) => setRegisters(res?.registers?.registers ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [productId, store]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!registerId) { setRegistrations([]); return; }
    store
      .ListDocumentRegistrations(registerId)
      .then((res: any) => setRegistrations(res?.registrations?.registrations ?? []))
      .catch(() => setRegistrations([]));
  }, [registerId, productId, store]);

  const thisDocRegistrations = registrations.filter((r) => r.document?.id === documentId);

  const register = () => {
    if (!registerId) return;
    setBusy(true);
    store
      .RegisterDocument(registerId, {
        register: { id: registerId, type_name: 'document_register', product_id: productId },
        document: documentTarget(documentId, productId),
        summary: summary.trim() || undefined,
      } as any)
      .then(() => {
        setSummary('');
        return store.ListDocumentRegistrations(registerId).then((res: any) =>
          setRegistrations(res?.registrations?.registrations ?? []),
        );
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  return (
    <SectionState loading={loading} error={error} empty={registers.length === 0} emptyText={t('document.register.noRegisters')}>
      <div className="space-y-3">
        <Select
          value={registerId}
          onChange={(e: any) => setRegisterId(e.target.value)}
          placeholder={t('document.register.pickRegister')}
          aria-label={t('document.register.pickRegister')}
        >
          {registers.map((r) => (
            <option key={r.entity?.id} value={r.entity?.id}>
              {r.name} · {t(registrationDirectionLabelKey(r.direction))}
            </option>
          ))}
        </Select>

        {registerId && (
          <div className="rounded-md border border-border p-2.5 space-y-2">
            {thisDocRegistrations.length > 0 ? (
              <div className="text-xs text-muted-foreground">
                {t('document.register.alreadyRegistered', {
                  number: thisDocRegistrations[0].registration_number,
                  date: formatDate(thisDocRegistrations[0].registration_date, locale),
                })}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder={t('document.register.summaryPlaceholder')}
                  className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
                />
                <button
                  type="button"
                  disabled={busy}
                  onClick={register}
                  className="w-full px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
                >
                  {busy ? t('common.saving') : t('document.register.registerHere')}
                </button>
              </>
            )}
          </div>
        )}

        {registerId && registrations.length > 0 && (
          <ul className="space-y-1">
            {registrations.slice(0, 12).map((r, i) => (
              <li
                key={r.entity?.id || i}
                className={`flex items-center gap-2 rounded-md border p-2 text-xs ${
                  r.document?.id === documentId ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <span className="font-mono text-foreground">{r.registration_number}</span>
                <span className="text-muted-foreground truncate flex-1">{r.summary}</span>
                <span className="text-muted-foreground">{formatDate(r.registration_date, locale)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionState>
  );
});

// ---------------------------------------------------------------------------
// Audit tab
// ---------------------------------------------------------------------------

const AuditTab = observer(({ documentId }: Omit<Props, 'onClose' | 'item' | 'onChanged' | 'productId'>) => {
  const { t, locale } = useI18n();
  const store = useDocument();
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    store
      .ListAuditLog('document', documentId)
      .then((res: any) => setLogs(res?.logs?.logs ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [documentId, store]);

  return (
    <SectionState loading={loading} error={error} empty={logs.length === 0} emptyText={t('document.auditEmpty')}>
      <ul className="space-y-1.5">
        {logs.map((l, i) => (
          <li key={l.entity?.id || i} className="flex gap-2 text-xs">
            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-foreground">
                {(l.action || '').replace(/^DOCUMENT_AUDIT_ACTION_/, '').toLowerCase().replace(/_/g, ' ')}
                {l.actor ? ` · ${actorName(l.actor)}` : ''}
              </div>
              {l.details && <div className="text-muted-foreground truncate">{l.details}</div>}
              <div className="text-[11px] text-muted-foreground">{formatRelativeTime(l.created_at, locale)}</div>
            </div>
          </li>
        ))}
      </ul>
    </SectionState>
  );
});

// ---------------------------------------------------------------------------
// Rail shell
// ---------------------------------------------------------------------------

const TABS: { id: RailTab; labelKey: string }[] = [
  { id: 'lifecycle', labelKey: 'document.rail.lifecycle' },
  { id: 'versions', labelKey: 'document.rail.versions' },
  { id: 'review', labelKey: 'document.rail.review' },
  { id: 'acks', labelKey: 'document.rail.acks' },
  { id: 'approval', labelKey: 'document.rail.approval' },
  { id: 'signing', labelKey: 'document.rail.signing' },
  { id: 'register', labelKey: 'document.rail.register' },
  { id: 'audit', labelKey: 'document.rail.audit' },
];

export const DocumentComplianceRail = observer(({ documentId, productId, item, reviewAnchor, onClose, onChanged, onSuggestionAccepted }: Props) => {
  const { t } = useI18n();
  const [tab, setTab] = useState<RailTab>('lifecycle');
  useEffect(() => {
    if (reviewAnchor) setTab('review');
  }, [reviewAnchor]);

  return (
    <aside className="w-full lg:w-[340px] flex-shrink-0 border-l border-rail-border bg-rail lg:h-[calc(100vh-3.5rem)] lg:sticky lg:top-14 overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b border-rail-border">
        <h2 className="text-sm font-semibold text-rail-foreground">{t('document.rail.title')}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-rail-border" role="tablist" aria-label={t('document.rail.title')}>
        {TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              tab === tb.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      <div className="p-4">
        {tab === 'lifecycle' && <LifecycleTab documentId={documentId} productId={productId} item={item} onChanged={onChanged} />}
        {tab === 'versions' && <DocumentVersionsPanel documentId={documentId} productId={productId} onChanged={onChanged} />}
        {tab === 'review' && (
          <DocumentReviewPanel
            documentId={documentId}
            documentVersion={parseDocumentVersion(getDocumentVersion(item)) || 'current'}
            selectedBlock={reviewAnchor}
            canAcceptSuggestions={canEditDocument(item)}
            onSuggestionAccepted={onSuggestionAccepted}
          />
        )}
        {tab === 'acks' && <AcksTab documentId={documentId} productId={productId} item={item} onChanged={onChanged} />}
        {tab === 'approval' && <ApprovalTab documentId={documentId} productId={productId} onChanged={onChanged} />}
        {tab === 'signing' && <DocumentSigningPanel documentId={documentId} productId={productId} />}
        {tab === 'register' && <RegisterTab documentId={documentId} productId={productId} />}
        {tab === 'audit' && <AuditTab documentId={documentId} />}
      </div>
    </aside>
  );
});

export default DocumentComplianceRail;
